import React, { useEffect, useMemo, useState, useCallback } from "react";
import api, { API } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Label } from "@/components/ui/label";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
    BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
} from "recharts";
import { Download, CalendarRange, Clock, Users as UsersIcon, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";

const RANGE_PRESETS = [
    { id: "7", label: "Last 7 days", days: 6 },
    { id: "30", label: "Last 30 days", days: 29 },
    { id: "90", label: "Last 90 days", days: 89 },
];

function iso(d) {
    return d.toISOString().slice(0, 10);
}

function defaultRange(days) {
    const end = new Date();
    const start = new Date();
    start.setDate(start.getDate() - days);
    return { start: iso(start), end: iso(end) };
}

function fmtHours(secs) {
    const h = Math.floor(secs / 3600);
    const m = Math.round((secs % 3600) / 60);
    if (h <= 0) return `${m}m`;
    return `${h}h ${m}m`;
}

function fmtShortDate(d) {
    const dt = new Date(`${d}T00:00:00Z`);
    return `${dt.getUTCMonth() + 1}/${dt.getUTCDate()}`;
}

/**
 * Shared attendance dashboard for admins & instructors.
 * - `programs` is the list they can pick from (admin: all; instructor: own)
 * - `initialProgramId` preselects a program (when deep-linked).
 */
export default function AttendanceBoard({
    programs = [],
    initialProgramId = null,
    heading = "Attendance",
    subheading = "See who showed up and for how long.",
}) {
    const [programId, setProgramId] = useState(initialProgramId || (programs[0]?.id ?? ""));
    const [rangeId, setRangeId] = useState("30");
    const [range, setRange] = useState(defaultRange(29));
    const [grid, setGrid] = useState(null);
    const [loading, setLoading] = useState(false);
    const [studentFilter, setStudentFilter] = useState("");

    // If parent lazily loads programs AND we received an initialProgramId, lock it in
    // once programs arrive. Also lock in the first program if nothing was picked.
    useEffect(() => {
        if (programId) return;
        if (initialProgramId && programs.some((p) => p.id === initialProgramId)) {
            setProgramId(initialProgramId);
        } else if (programs[0]?.id) {
            setProgramId(programs[0].id);
        }
    }, [programs, initialProgramId, programId]);

    useEffect(() => {
        const preset = RANGE_PRESETS.find((r) => r.id === rangeId);
        if (preset) setRange(defaultRange(preset.days));
    }, [rangeId]);

    const load = useCallback(async () => {
        if (!programId) return;
        setLoading(true);
        try {
            const { data } = await api.get("/admin/attendance", {
                params: { program_id: programId, start: range.start, end: range.end },
            });
            setGrid(data);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Could not load attendance.");
        } finally {
            setLoading(false);
        }
    }, [programId, range.start, range.end]);

    useEffect(() => {
        load();
    }, [load]);

    const filteredStudents = useMemo(() => {
        const list = grid?.students || [];
        const q = studentFilter.trim().toLowerCase();
        if (!q) return list;
        return list.filter(
            (s) =>
                (s.name || "").toLowerCase().includes(q) ||
                (s.email || "").toLowerCase().includes(q),
        );
    }, [grid, studentFilter]);

    const rowsByUser = useMemo(() => {
        const map = {};
        (grid?.rows || []).forEach((r) => {
            map[r.user_id] = r;
        });
        return map;
    }, [grid]);

    const kpis = useMemo(() => {
        if (!grid)
            return { students: 0, totalPresent: 0, totalAbsent: 0, totalSeconds: 0, avgMinutesPerStudent: 0 };
        let present = 0,
            absent = 0,
            seconds = 0;
        (grid.rows || []).forEach((r) => {
            present += r.totals.present;
            absent += r.totals.absent;
            seconds += r.totals.active_seconds;
        });
        const studentCount = (grid.students || []).length;
        return {
            students: studentCount,
            totalPresent: present,
            totalAbsent: absent,
            totalSeconds: seconds,
            avgMinutesPerStudent: studentCount
                ? Math.round(seconds / studentCount / 60)
                : 0,
        };
    }, [grid]);

    const chartData = useMemo(() => {
        if (!grid) return [];
        const dayTotals = {};
        (grid.days || []).forEach((d) => {
            dayTotals[d] = { date: d, minutes: 0, present: 0 };
        });
        (grid.rows || []).forEach((r) => {
            Object.entries(r.cells || {}).forEach(([d, cell]) => {
                if (!dayTotals[d]) return;
                dayTotals[d].minutes += Math.round((cell.active_seconds || 0) / 60);
                if (cell.status === "present") dayTotals[d].present += 1;
            });
        });
        return Object.values(dayTotals);
    }, [grid]);

    const exportXlsx = () => {
        if (!programId) return;
        const token = localStorage.getItem("token");
        const url = `${API}/admin/attendance/export?program_id=${encodeURIComponent(
            programId,
        )}&start=${range.start}&end=${range.end}&auth=${encodeURIComponent(token)}`;
        window.open(url, "_blank");
    };

    const thresholdMin = (grid?.threshold_seconds || 600) / 60;

    return (
        <div className="space-y-6" data-testid="attendance-board">
            <div className="flex items-start justify-between flex-wrap gap-4">
                <div>
                    <div className="flex items-center gap-2 text-xs uppercase tracking-[0.18em] text-accent font-semibold">
                        <CalendarRange className="h-3.5 w-3.5" />
                        Attendance
                    </div>
                    <h1 className="font-display text-3xl text-primary" data-testid="attendance-heading">
                        {heading}
                    </h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                        {subheading} A student counts as <strong>Present</strong> on a given day
                        if they study for at least {thresholdMin} minutes.
                    </p>
                </div>
                <Button
                    onClick={exportXlsx}
                    disabled={!programId}
                    variant="outline"
                    className="gap-2"
                    data-testid="attendance-export-btn"
                >
                    <Download className="h-4 w-4" />
                    Download Excel
                </Button>
            </div>

            {/* Controls */}
            <Card className="border-border">
                <CardContent className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
                    <div>
                        <Label className="text-xs uppercase tracking-wider">Program</Label>
                        <Select value={programId} onValueChange={setProgramId}>
                            <SelectTrigger data-testid="attendance-program-trigger">
                                <SelectValue placeholder="Pick a program" />
                            </SelectTrigger>
                            <SelectContent>
                                {programs.map((p) => (
                                    <SelectItem key={p.id} value={p.id}>
                                        {p.title}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label className="text-xs uppercase tracking-wider">Range</Label>
                        <Select value={rangeId} onValueChange={setRangeId}>
                            <SelectTrigger data-testid="attendance-range-trigger">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {RANGE_PRESETS.map((r) => (
                                    <SelectItem key={r.id} value={r.id}>
                                        {r.label}
                                    </SelectItem>
                                ))}
                                <SelectItem value="custom">Custom…</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    {rangeId === "custom" ? (
                        <>
                            <div>
                                <Label className="text-xs uppercase tracking-wider">Start</Label>
                                <Input
                                    type="date"
                                    value={range.start}
                                    max={range.end}
                                    onChange={(e) =>
                                        setRange((r) => ({ ...r, start: e.target.value }))
                                    }
                                    data-testid="attendance-start-input"
                                />
                            </div>
                            <div>
                                <Label className="text-xs uppercase tracking-wider">End</Label>
                                <Input
                                    type="date"
                                    value={range.end}
                                    min={range.start}
                                    onChange={(e) =>
                                        setRange((r) => ({ ...r, end: e.target.value }))
                                    }
                                    data-testid="attendance-end-input"
                                />
                            </div>
                        </>
                    ) : (
                        <div className="md:col-span-2">
                            <Label className="text-xs uppercase tracking-wider">Filter students</Label>
                            <Input
                                value={studentFilter}
                                onChange={(e) => setStudentFilter(e.target.value)}
                                placeholder="Search by name or email"
                                data-testid="attendance-filter-input"
                            />
                        </div>
                    )}
                </CardContent>
            </Card>

            {/* KPIs */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <KpiCard
                    icon={UsersIcon}
                    label="Enrolled students"
                    value={kpis.students}
                    testId="kpi-students"
                />
                <KpiCard
                    icon={CheckCircle2}
                    label="Present days"
                    value={kpis.totalPresent}
                    accent="text-primary"
                    testId="kpi-present"
                />
                <KpiCard
                    icon={XCircle}
                    label="Absent days"
                    value={kpis.totalAbsent}
                    accent="text-destructive"
                    testId="kpi-absent"
                />
                <KpiCard
                    icon={Clock}
                    label="Avg · minutes per student"
                    value={kpis.avgMinutesPerStudent}
                    suffix="m"
                    testId="kpi-avg-minutes"
                />
            </div>

            {/* Chart */}
            <Card className="border-border">
                <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                        <div>
                            <h2 className="font-display text-lg text-primary">
                                Daily study minutes (all students)
                            </h2>
                            <p className="text-xs text-muted-foreground">
                                Hover any bar to see exact minutes and number Present.
                            </p>
                        </div>
                    </div>
                    <div style={{ width: "100%", height: 240 }} data-testid="attendance-chart">
                        <ResponsiveContainer>
                            <BarChart data={chartData} margin={{ top: 8, right: 8, left: 0, bottom: 8 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis
                                    dataKey="date"
                                    tickFormatter={fmtShortDate}
                                    fontSize={11}
                                    stroke="#94a3b8"
                                />
                                <YAxis fontSize={11} stroke="#94a3b8" allowDecimals={false} />
                                <Tooltip
                                    formatter={(v, k) =>
                                        k === "minutes" ? [`${v} min`, "Total minutes"] : [v, "Students present"]
                                    }
                                    labelFormatter={(d) => new Date(`${d}T00:00:00Z`).toDateString()}
                                />
                                <Bar dataKey="minutes" fill="#103F26" radius={[4, 4, 0, 0]} />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </CardContent>
            </Card>

            {/* Calendar grid */}
            <Card className="border-border">
                <CardContent className="p-0">
                    <div className="px-4 py-3 border-b border-border flex items-center justify-between">
                        <div>
                            <h2 className="font-display text-lg text-primary">
                                Calendar — Present / Absent per student
                            </h2>
                            <div className="flex gap-3 text-xs text-muted-foreground mt-1">
                                <LegendChip color="bg-primary/70" label="Present" />
                                <LegendChip color="bg-destructive/40" label="Absent" />
                                <LegendChip color="bg-muted" label="Before enrolment" />
                            </div>
                        </div>
                        <div className="text-xs text-muted-foreground">
                            {loading ? "Loading…" : `${filteredStudents.length} students`}
                        </div>
                    </div>

                    {!programId ? (
                        <div className="p-10 text-center text-sm text-muted-foreground">
                            Pick a program above to see attendance.
                        </div>
                    ) : filteredStudents.length === 0 && !loading ? (
                        <div className="p-10 text-center text-sm text-muted-foreground">
                            No students match your filter yet.
                        </div>
                    ) : (
                        <div className="overflow-x-auto" data-testid="attendance-calendar">
                            <table className="min-w-full text-xs">
                                <thead className="bg-secondary/30">
                                    <tr>
                                        <th className="sticky left-0 z-10 bg-secondary/30 px-3 py-2 text-left font-semibold min-w-[220px]">
                                            Student
                                        </th>
                                        <th className="px-2 py-2 text-center font-semibold">P</th>
                                        <th className="px-2 py-2 text-center font-semibold">A</th>
                                        <th className="px-2 py-2 text-center font-semibold">Total</th>
                                        {(grid?.days || []).map((d) => (
                                            <th
                                                key={d}
                                                className="px-1.5 py-2 text-center font-medium text-muted-foreground"
                                                title={new Date(`${d}T00:00:00Z`).toDateString()}
                                            >
                                                {fmtShortDate(d)}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredStudents.map((s) => {
                                        const row = rowsByUser[s.user_id];
                                        return (
                                            <tr
                                                key={s.user_id}
                                                className="border-t border-border hover:bg-secondary/10"
                                                data-testid={`attendance-row-${s.user_id}`}
                                            >
                                                <td className="sticky left-0 bg-background px-3 py-2 font-medium">
                                                    <div className="truncate max-w-[200px]">
                                                        {s.name}
                                                    </div>
                                                    <div className="truncate text-muted-foreground text-[11px] max-w-[200px]">
                                                        {s.email}
                                                    </div>
                                                    {s.payment_mode === "paid_offline" && (
                                                        <Badge
                                                            variant="outline"
                                                            className="mt-1 text-[10px] py-0 h-4 border-accent/40"
                                                        >
                                                            Offline
                                                        </Badge>
                                                    )}
                                                </td>
                                                <td className="px-2 py-2 text-center text-primary font-semibold">
                                                    {row?.totals.present ?? 0}
                                                </td>
                                                <td className="px-2 py-2 text-center text-destructive/80">
                                                    {row?.totals.absent ?? 0}
                                                </td>
                                                <td className="px-2 py-2 text-center text-muted-foreground">
                                                    {fmtHours(row?.totals.active_seconds ?? 0)}
                                                </td>
                                                {(grid?.days || []).map((d) => {
                                                    const cell = row?.cells?.[d] || { status: "absent", active_seconds: 0 };
                                                    let bg = "bg-destructive/20";
                                                    if (cell.status === "present") bg = "bg-primary/70 text-primary-foreground";
                                                    if (cell.status === "pre_enrolment") bg = "bg-muted";
                                                    const mins = Math.round((cell.active_seconds || 0) / 60);
                                                    const title =
                                                        cell.status === "pre_enrolment"
                                                            ? "Before enrolment"
                                                            : cell.status === "present"
                                                              ? `Present · ${mins} min`
                                                              : `Absent · ${mins} min`;
                                                    return (
                                                        <td key={d} className="p-0.5">
                                                            <div
                                                                className={`w-6 h-6 mx-auto rounded-sm ${bg} flex items-center justify-center text-[9px] font-semibold`}
                                                                title={title}
                                                            >
                                                                {cell.status === "present" ? "P" : cell.status === "absent" ? "A" : "—"}
                                                            </div>
                                                        </td>
                                                    );
                                                })}
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </CardContent>
            </Card>
        </div>
    );
}

function KpiCard({ icon: Icon, label, value, suffix = "", accent = "text-primary", testId }) {
    return (
        <Card className="border-border">
            <CardContent className="p-4">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-muted-foreground">
                    <Icon className={`h-3.5 w-3.5 ${accent}`} />
                    {label}
                </div>
                <div
                    className={`mt-2 font-display text-3xl ${accent}`}
                    data-testid={testId}
                >
                    {value}
                    {suffix && <span className="text-base ml-0.5">{suffix}</span>}
                </div>
            </CardContent>
        </Card>
    );
}

function LegendChip({ color, label }) {
    return (
        <span className="inline-flex items-center gap-1.5">
            <span className={`inline-block w-3 h-3 rounded-sm ${color}`} />
            {label}
        </span>
    );
}
